import { forwardRef, useEffect, useImperativeHandle, useRef, type ClipboardEvent, type KeyboardEvent } from 'react';
import type { PlaceholderLabel } from '@immo/components';
import { cn } from '@immo/ui-kit';
import {
  adjacentChip,
  hydrateChips,
  insertNodesAt,
  serialiseChips,
  trackCaret,
  type ChipInsertHandle,
} from './email-template-chips';
import { attachOpenFieldInteraction } from './open-field-interaction';

export type ChipLineFieldProps = {
  // Canonical markup, applied once on mount: re-assigning `innerHTML` on every edit would reset the caret.
  readonly defaultValue: string;
  readonly labels: PlaceholderLabel;
  // Fires with the canonical markup (chips as empty spans) on every edit.
  readonly onChange: (markup: string) => void;
  readonly onFocus?: () => void;
  readonly ariaLabel: string;
  readonly invalid?: boolean;
  readonly describedBy?: string;
  readonly id?: string;
  // Counted over the visible text, chips included. Typing and pasting stop at it; insertion through the handle does not.
  readonly maxLength?: number;
  // `inline` is the borderless look of the e-mail window's header rows.
  readonly variant?: 'boxed' | 'inline';
};

/**
 * A single-line, chip-capable field in the kit `Input` chrome (the E-Mail-Vorlage's
 * Betreff). Enter is suppressed, paste arrives as plain text, and a chip leaves
 * whole on Backspace or Delete.
 */
export const ChipLineField = forwardRef<ChipInsertHandle, ChipLineFieldProps>(function ChipLineField(
  {
    defaultValue,
    labels,
    onChange,
    onFocus,
    ariaLabel,
    invalid = false,
    describedBy,
    id,
    maxLength,
    variant = 'boxed',
  },
  handle,
) {
  const ref = useRef<HTMLDivElement>(null);
  const savedRange = useRef<Range | null>(null);
  const initial = useRef({ defaultValue, labels });

  useEffect(() => {
    const root = ref.current;

    if (root === null) return undefined;

    hydrateChips(root, initial.current.defaultValue, initial.current.labels);

    return trackCaret(root, (range) => {
      savedRange.current = range;
    });
  }, []);

  const emit = () => {
    if (ref.current !== null) onChange(serialiseChips(ref.current));
  };

  // The interaction is attached once; this keeps it calling the latest `onChange`.
  const emitRef = useRef(emit);
  const maxLengthRef = useRef(maxLength);

  useEffect(() => {
    emitRef.current = emit;
    maxLengthRef.current = maxLength;
  });

  useEffect(() => {
    const root = ref.current;

    if (root === null) return undefined;

    const detachOpenFields = attachOpenFieldInteraction(root, () => emitRef.current());
    const onBeforeInput = (event: InputEvent) => {
      const limit = maxLengthRef.current;

      if (limit === undefined || event.inputType !== 'insertText') return;

      const selected = document.getSelection()?.toString().length ?? 0;

      if ((root.textContent?.length ?? 0) - selected + (event.data?.length ?? 0) > limit) event.preventDefault();
    };

    root.addEventListener('beforeinput', onBeforeInput);

    return () => {
      detachOpenFields();
      root.removeEventListener('beforeinput', onBeforeInput);
    };
  }, []);

  useImperativeHandle(handle, () => ({
    focus: () => ref.current?.focus(),
    insertNodesAtCaret: (nodes) => {
      if (ref.current === null) return;

      savedRange.current = insertNodesAt(ref.current, savedRange.current, nodes);
      emit();
    },
    root: () => ref.current,
  }));

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();

      return;
    }

    if (ref.current === null || (event.key !== 'Backspace' && event.key !== 'Delete')) return;

    const chip = adjacentChip(ref.current, event.key === 'Backspace' ? 'before' : 'after');

    if (chip === null) return;

    event.preventDefault();
    chip.remove();
    emit();
  };

  const onPaste = (event: ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();

    const pasted = event.clipboardData.getData('text/plain').replace(/\s*[\r\n]+\s*/g, ' ');
    const room =
      maxLength === undefined
        ? pasted.length
        : Math.max(
            0,
            maxLength - (ref.current?.textContent?.length ?? 0) + (document.getSelection()?.toString().length ?? 0),
          );
    const text = pasted.slice(0, room);

    if (text === '' || ref.current === null) return;

    savedRange.current = insertNodesAt(ref.current, savedRange.current, [document.createTextNode(text)]);
    emit();
  };

  return (
    <div
      ref={ref}
      role="textbox"
      aria-multiline="false"
      {...(id !== undefined ? { id } : {})}
      aria-label={ariaLabel}
      aria-invalid={invalid}
      {...(describedBy !== undefined ? { 'aria-describedby': describedBy } : {})}
      contentEditable
      suppressContentEditableWarning
      onInput={emit}
      onKeyDown={onKeyDown}
      onPaste={onPaste}
      {...(onFocus !== undefined ? { onFocus } : {})}
      className={
        variant === 'inline'
          ? 'w-full overflow-x-auto whitespace-nowrap bg-transparent py-1 text-base-sm text-text-primary outline-none'
          : cn(
              'flex h-control-md w-full items-center overflow-x-auto whitespace-nowrap rounded-md border bg-surface px-3 text-base font-sans text-text-primary',
              'transition-colors duration-micro ease-out',
              'focus-visible:outline-none focus-visible:border-interactive focus-visible:shadow-focus',
              invalid
                ? 'border-danger-500 focus-visible:border-danger-500 focus-visible:shadow-focus-danger'
                : 'border-border',
            )
      }
    />
  );
});
